import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Alert, Button, InfoSheet, Paragraph, Separator, useOverlay } from '@fs/zion-ui'
import { useTranslation, Trans } from 'react-i18next'
import { useFeatureFlag } from '@fs/zion-flags'
import { useHelpTutorial } from '@fs/zion-help-tutorial'
import { getPreference } from '@fs/zion-preferences'
import AttachImageToTree from '@fs/zion-attach-image-to-tree'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import NoRecordNamesOnDocument from './components/NoRecordNamesOnDocument'
import AttachImageWithoutTreeUpdatesButton from './components/AttachImageWithoutTreeUpdatesButton'
import AddNamesPanel from '../../../../views/fullText/components/addNamesPanel/AddNamesPanel'
import { ADD_NAME_PANEL_REFERRERS } from '../../../../views/fullText/components/addNamesPanel/utils/utils'
import AttachFlowTutorial from '../../../../views/fullText/components/attachFlowTutorial'
import useAttachImageToTreeData from '../../../../hooks/useAttachImageToTreeData'
import DisableAttachFlowFlagOverlay from './components/DisableAttachFlowFlagOverlay'
import HouseholdList from '../../householdList/HouseholdList'
import useHouseholdList from '../../householdList/hooks/useHouseholdsList'
import IndexImage from '../../../../views/indexing/components/indexImage/IndexImage'

export default function SelectPersonToAttachToFamilyTreeInfoSheet({
  overlay,
  loading,
  imageTitle,
  fullTextAttachFlowV1IsOn = false,
}) {
  const { t } = useTranslation()
  const title = t('fullText.addNamesPanel.attachToTree.title', 'Attach to Family Tree')
  const description = t(
    'attachToFamilyTreeNamesList.paragraph.description',
    'Adding or selecting a name will include document details from this image to update Family Tree. You can also just attach the image as a source.'
  )
  const [filterValue, setFilterValue] = useState('')
  const [showTutorial, setShowTutorial] = useState(false)
  const [tutorialKey, setTutorialKey] = useState(0)
  const [isAlertDismissed, setIsAlertDismissed] = useState(false)
  const [selectedRecordId, setSelectedRecordId] = useState(null)
  const fullTextAttachFlowFlagOptIn = useFeatureFlag('rip_sharedImageViewer_fullTextAttachFlowOptIn')

  const attachFlowTutorialHook = useHelpTutorial('fullText.attachFlow.tutorial', true)
  const { attachToTreeOverlay } = useOverlayContext()
  const disableAttachFlowFlagOverlay = useOverlay()

  // Handler to reset showTutorial when tutorial closes
  const handleTutorialClose = useCallback(() => {
    attachFlowTutorialHook.close()
    setShowTutorial(false)
  }, [attachFlowTutorialHook])

  // Override to prevent ad popup and reset state on close
  const attachFlowTutorialWithoutAd = useMemo(
    () => ({
      ...attachFlowTutorialHook,
      shouldShowAd: false,
      close: handleTutorialClose,
    }),
    [attachFlowTutorialHook, handleTutorialClose]
  )

  const [isFirstTime, setIsFirstTime] = useState(null) // null = checking, true/false = checked

  // Check if this is the user's first time on mount
  useEffect(() => {
    const checkFirstTime = async () => {
      try {
        const adPreferences = await getPreference('help-tutorial.show-ad-prefs')
        const tutorialPreference = adPreferences?.['fullText.attachFlow.tutorial']
        const firstTime = tutorialPreference !== false

        setIsFirstTime(firstTime)

        if (firstTime) {
          setShowTutorial(true)
          // Save preference to prevent showing again
          attachFlowTutorialHook.changeTutorialPreference(false)
        }
      } catch (error) {
        console.error('[AttachToTree] Failed to check tutorial preference:', error)
        setIsFirstTime(false)
      }
    }

    if (overlay.isOpen && isFirstTime === null) {
      checkFirstTime()
    }
  }, [overlay.isOpen, isFirstTime, attachFlowTutorialHook])

  const attachImageToTreeData = useAttachImageToTreeData(imageTitle)

  const { householdListProps, showBlankState } = useHouseholdList({
    loading,
    filterValue,
  })
  const addedInfoSheetProps = {
    title,
    headingColor: 'green',
    size: 'lg',
  }

  return (
    <InfoSheet {...overlay} {...addedInfoSheetProps}>
      {fullTextAttachFlowV1IsOn && (
        <>
          {!fullTextAttachFlowFlagOptIn.isOn && (
            <Alert type="help" dense dismissable outlined onDismiss={() => setIsAlertDismissed(true)}>
              <Paragraph size="xs">
                {t(
                  'attachFlow.alert.description',
                  'The information from this historical document can improve your tree.'
                )}
              </Paragraph>
              <Button
                variant="link"
                onClick={() => {
                  setShowTutorial(true)
                  setTutorialKey((prev) => prev + 1)
                }}
                linkName="attach-flow-learn-how"
              >
                {t('attachFlow.alert.learnHow', 'LEARN HOW')}
              </Button>{' '}
            </Alert>
          )}
          {fullTextAttachFlowFlagOptIn.isOn && (
            <Alert type="help" dense dismissable outlined onDismiss={() => setIsAlertDismissed(true)}>
              <Paragraph size="xs">
                <Trans i18nKey="attachFlow.try-feature-alert.description">
                  This is a new flow. You can use the old flow by selecting{' '}
                  <strong>Attach Image Without Tree Updates</strong> or disabling this feature.
                </Trans>
              </Paragraph>
              <Button
                variant="link"
                onClick={() => {
                  disableAttachFlowFlagOverlay.open()
                }}
                linkName="attach-flow-disable-feature"
              >
                {t('attachFlow.try-feature-alert.disable-feature', 'Disable Feature')}
              </Button>{' '}
            </Alert>
          )}
          {!isAlertDismissed && <Separator size="xs" />}
        </>
      )}
      {showBlankState ? (
        <NoRecordNamesOnDocument />
      ) : (
        <>
          <HouseholdList
            description={description}
            setSelectedRecordId={setSelectedRecordId}
            filterValue={filterValue}
            setFilterValue={setFilterValue}
            showManageIndexesButton={false}
            {...householdListProps}
          />
          <AttachImageWithoutTreeUpdatesButton />
        </>
      )}
      {/* Since this is a different InfoSheet we need to render this panel to show personal details when clicking a name */}
      <IndexImage />
      <DisableAttachFlowFlagOverlay overlay={disableAttachFlowFlagOverlay} />
      <AddNamesPanel
        referrer={ADD_NAME_PANEL_REFERRERS.ATTACH_TO_TREE}
        recordId={selectedRecordId}
        setSelectedRecordId={setSelectedRecordId}
      />
      {showTutorial && <AttachFlowTutorial key={tutorialKey} helpTutorial={attachFlowTutorialWithoutAd} />}
      <AttachImageToTree {...attachImageToTreeData} attachToTreeFirstPanel={attachToTreeOverlay} />
    </InfoSheet>
  )
}
